// =============================================================================
// Cut & Crimp made-to-measure cable configurator.
//
// One ES module, no build step, no framework. It fetches everything it knows
// from the hub API and decides nothing itself: every rate, spec and limit comes
// down the wire, so repricing is a data edit in hme-admin and never a deploy.
//
// NEVER hardcode a price, rate or specification in this file. If you find
// yourself typing a number that a customer could be charged for, it belongs in
// the hub.
//
// The maths lives in cc-pricing.js, which is generated from
// hme-admin/lib/configurator/pricing.mjs and is the same file that is unit
// tested there. `npm run test` in hme-admin fails if the two have drifted.
// =============================================================================

import { priceAssembly } from "./cc-pricing.js";

const CACHE_KEY = "cc-configurator-payload";
const CACHE_TTL_MS = 10 * 60 * 1000;
const STUDS = ["M6", "M8", "M10", "M12"];

// ---------------------------------------------------------------------------
// Data loading. A stale cache is only ever used when its version matches, and
// a failure leaves the server-rendered fallback in place rather than showing a
// broken tool.
// ---------------------------------------------------------------------------
async function loadPayload(apiUrl) {
  let cached = null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (raw) cached = JSON.parse(raw);
  } catch {
    // Private browsing, or a corrupted entry. Not worth reporting.
  }

  if (cached && Date.now() - cached.at < CACHE_TTL_MS && cached.payload?.version) {
    return cached.payload;
  }

  const res = await fetch(apiUrl, { headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`configurator API ${res.status}`);
  const payload = await res.json();

  // A response with no version is not something to price from.
  if (!payload?.version || !Array.isArray(payload.families)) {
    throw new Error("configurator API returned an unexpected shape");
  }

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), payload }));
  } catch {
    // Storage full or blocked. The tool still works, it just refetches.
  }
  return payload;
}

// ---------------------------------------------------------------------------
// What can physically be crimped onto what.
//
// A stud we do not keep on the shelf stays selectable with a lead time. One
// that cannot be crimped is switched off with the reason on the button, so
// nobody picks it and is then refused. Not stocking a lug is a supply problem;
// not being able to crimp one is physics.
// ---------------------------------------------------------------------------
function studReason(data, csa, stud) {
  const avail = data.studs.availability[String(csa)];
  if (avail && !Object.values(avail).some(Boolean)) return "no lugs this small";
  if (stud === "M12" && csa < 35) return "M12 starts at 35mm²";
  if (stud === "M6" && csa > 50) return "palm too small";
  if (stud === "M8" && csa > 95) return "too small for this size";
  return null;
}
const studFits = (data, csa, stud) => stud === "cut" || studReason(data, csa, stud) === null;

function nearestFittable(data, csa, stud) {
  if (studFits(data, csa, stud)) return stud;
  const i = STUDS.indexOf(stud);
  for (let d = 1; d < STUDS.length; d++) {
    for (const j of [i - d, i + d]) {
      if (j >= 0 && j < STUDS.length && studFits(data, csa, STUDS[j])) return STUDS[j];
    }
  }
  return "cut";
}

// ---------------------------------------------------------------------------
class Configurator {
  constructor(root, data) {
    this.root = root;
    this.data = data;
    this.state = {
      family: data.families[0]?.key,
      csa: data.families[0]?.cables[0]?.csa,
      colour: "black",
      lengthMm: 2000,
      endA: "M8",
      endB: "M10",
      notices: [],
    };
    this.readUrl();
    this.reconcileEnds();
    this.mount();
    this.render();
  }

  fam() { return this.data.families.find((f) => f.key === this.state.family) ?? this.data.families[0]; }
  cable() { return this.fam().cables.find((c) => c.csa === this.state.csa) ?? this.fam().cables[0]; }

  cfg() {
    return {
      vatRate: this.data.store.vatRate,
      roundingStepIncVat: this.data.store.roundingStepIncVat,
      baseFeeExVat: this.data.pricing.baseFeeExVat,
      lengthAllowanceM: this.data.pricing.lengthAllowanceM,
      narrowPalmUpgradeExVat: this.data.pricing.narrowPalmUpgradeExVat,
      fittedEndPrices: this.data.pricing.fittedEndPrices,
      rates: Object.fromEntries(
        this.data.families.map((f) => [f.key, Object.fromEntries(f.cables.map((c) => [String(c.csa), c.ratePerMExVat]))]),
      ),
    };
  }

  // -- url ------------------------------------------------------------------
  readUrl() {
    const q = new URLSearchParams(location.search);
    const f = q.get("f");
    if (this.data.families.some((x) => x.key === f)) this.state.family = f;

    const csa = Number(q.get("csa"));
    this.state.csa = this.fam().cables.some((c) => c.csa === csa) ? csa : this.fam().cables[0].csa;

    const col = q.get("col");
    if (["red", "black", "pair"].includes(col)) this.state.colour = col;

    const len = Number(q.get("len"));
    if (Number.isFinite(len) && len >= 100 && len <= this.data.pricing.maxLengthMm) this.state.lengthMm = len;

    for (const [k, p] of [["endA", "a"], ["endB", "b"]]) {
      const v = q.get(p);
      if (v === "cut" || STUDS.includes(v)) this.state[k] = v;
    }
    // An invalid value falls back to a default rather than throwing, because a
    // link pasted into an email gets mangled more often than anyone expects.
  }

  writeUrl() {
    const s = this.state;
    const q = new URLSearchParams({ f: s.family, csa: s.csa, col: s.colour, len: s.lengthMm, a: s.endA, b: s.endB });
    history.replaceState(null, "", `${location.pathname}?${q}`);
  }

  // -- end reconciliation ---------------------------------------------------
  reconcileEnds() {
    this.state.notices = [];
    for (const [side, name] of [["endA", "End A"], ["endB", "End B"]]) {
      const was = this.state[side];
      if (studFits(this.data, this.state.csa, was)) continue;
      const why = studReason(this.data, this.state.csa, was);
      const now = nearestFittable(this.data, this.state.csa, was);
      this.state[side] = now;
      this.state.notices.push({
        level: "info",
        t: `${name} changed from ${was} to ${now === "cut" ? "unterminated" : now}`,
        d: why === "no lugs this small"
          ? `No lug is made for ${this.state.csa}mm² cable, so this size is sold cut to length with bare ends.`
          : `A ${was} lug cannot be crimped onto ${this.state.csa}mm² cable (${why}).${now === "cut" ? "" : ` ${now} is the closest that can.`}`,
      });
    }
  }

  // -- validation -----------------------------------------------------------
  validate(p) {
    const d = this.data, c = this.cable(), s = this.state, out = [];
    const min = d.pricing.minLengthMm[String(c.csa)] ?? 150;

    if (s.lengthMm < min) out.push({ level: "stop", t: `Too short for ${c.csa}mm²`,
      d: `Below ${min}mm the two boots meet and the cable cannot take its bend radius. We cannot make this.`,
      fix: { label: `Set ${min}mm`, run: () => { s.lengthMm = min; } } });

    if (s.lengthMm > d.pricing.maxLengthMm) out.push({ level: "stop", t: "Longer than we cut online",
      d: "Over 20 metres we would rather talk it through than guess. Give us a ring." });

    for (const [name, v] of [["End A", s.endA], ["End B", s.endB]]) {
      if (v === "cut") continue;
      const before = out.length;
      if (v === "M12" && c.csa < 35) out.push({ level: "stop", t: `${name}: M12 lug will not fit ${c.csa}mm²`,
        d: "The barrel is too big to crimp reliably onto this cable, so the crimp would be the weakest part of the circuit." });
      if (v === "M6" && c.csa > 50) out.push({ level: "stop", t: `${name}: M6 lug will not carry ${c.csa}mm²`,
        d: "The palm is too small for the current this cable is rated to. It would run hot at the terminal." });

      const blockedHere = out.length > before;
      if (!blockedHere && d.studs.availability[String(c.csa)]?.[v] === false) {
        out.push({ level: "info", t: `${name}: ${v} is not a stock item at ${c.csa}mm²`,
          d: "Not a stock line in this size. We will give you the date when the manufacturer gives it to us, and we will not guess in the meantime. Allow two to three working days." });
      }
    }

    if (c.csa === 50 && (s.endA === "M6" || s.endB === "M6"))
      out.push({ level: "warn", t: "Palm may foul a recessed terminal",
        d: "50mm² on an M6 stud will physically fit. The palm may still foul a recessed terminal, which is why we stock the narrow-palm version and will tell you when you need it." });
    if (c.csa === 70 && (s.endA === "M8" || s.endB === "M8"))
      out.push({ level: "warn", t: "Palm may foul a recessed terminal",
        d: "70mm² on an M8 stud will physically fit. The palm may still foul a recessed terminal, which is why we stock the narrow-palm version and will tell you when you need it." });

    if (s.endA === "cut" && s.endB === "cut")
      out.push({ level: "warn", t: "That is just cut cable",
        d: "With no lug on either end you are paying a making charge for a straight cut. Buying it by the metre is cheaper." });

    const kg = p.billedLengthM * c.kgPerM;
    if (kg > 30) out.push({ level: "stop", t: `Too heavy to post (${kg.toFixed(1)}kg)`,
      d: "Over 30kg needs a pallet. Ring us and we will quote carriage." });
    else if (kg > 20) out.push({ level: "warn", t: `Heavy parcel (${kg.toFixed(1)}kg)`,
      d: "Over 20kg goes on a two-man service, so delivery takes an extra day." });

    if (p.priceIncVat > 800) out.push({ level: "stop", t: "Above our online limit",
      d: "Assemblies over £800 are quoted by hand so we can check the specification with you first." });

    return out;
  }

  // -- markup ---------------------------------------------------------------
  mount() {
    // Single column, and the price sits at the BOTTOM rather than in a side
    // rail. The side rail overlapped the End B picker at some widths, and a
    // configurator that clips the control you are using is worse than one that
    // makes you scroll. A compact bar pinned to the bottom of the screen keeps
    // the price visible the whole way down, and hides itself once the full
    // breakdown scrolls into view so the total is never shown twice.
    this.root.innerHTML = `
      <div class="cc-layout">
        <div class="cc-viz"><svg data-r="diagram" viewBox="0 0 700 210" role="img" aria-labelledby="cc-diagram-desc"></svg>
          <p id="cc-diagram-desc" class="cc-sr" data-r="diagramDesc"></p></div>
        <div class="cc-steps">
          <section class="cc-step"><header><span class="cc-step-n">01</span><h3>Cable</h3></header>
            <div class="cc-opts" data-r="family"></div><p class="cc-sub" data-r="familyNote"></p></section>
          <section class="cc-step"><header><span class="cc-step-n">02</span><h3>Size</h3><span class="cc-hint">Free air rating shown</span></header>
            <div class="cc-opts" data-r="csa"></div><p class="cc-sub" data-r="csaSpec"></p>
            <p class="cc-sub">Not sure which size? Our <a href="/pages/cable-size-calculator">cable size calculator</a>
            works it out from your load and run length, checks volt drop and current rating together, and sends the
            answer back here.</p></section>
          <section class="cc-step"><header><span class="cc-step-n">03</span><h3>Colour</h3></header>
            <div class="cc-opts" data-r="colour"></div></section>
          <section class="cc-step"><header><span class="cc-step-n">04</span><h3>Length</h3></header>
            <div class="cc-lenrow">
              <button type="button" class="cc-stepper" data-len="-10" aria-label="Decrease length by 10mm">&minus;</button>
              <label class="cc-sr" for="cc-len">Length in millimetres</label>
              <input type="number" id="cc-len" data-r="lenInput" min="100" step="10">
              <span class="cc-mono cc-muted">mm</span>
              <button type="button" class="cc-stepper" data-len="10" aria-label="Increase length by 10mm">+</button>
              <input type="range" data-r="lenRange" min="100" step="10" aria-label="Length slider">
            </div>
            <p class="cc-sub">Measured between terminal hole centres, or from the hole centre to the cut end where one end is unterminated. Finished tolerance &plusmn;2%.</p>
            <p class="cc-sub cc-mono" data-r="lenFacts"></p></section>
          <section class="cc-step"><header><span class="cc-step-n">05</span><h3>Ends</h3><span class="cc-hint">Priced on cable size, not stud size</span></header>
            <div class="cc-ends">
              <div class="cc-end"><h4>End A</h4><div class="cc-opts" data-r="endA"></div></div>
              <div class="cc-end"><h4>End B</h4><div class="cc-opts" data-r="endB"></div>
                <button type="button" class="cc-ghost" data-same>Same as End A</button></div>
            </div>
            <p class="cc-sub" data-r="endsNote"></p></section>
        </div>
        <div class="cc-msgs" data-r="msgs" aria-live="polite"></div>
        <div class="cc-price" data-r="priceCard">
          <div class="cc-price-head"><div class="cc-big" data-r="priceBig">&mdash;</div>
            <div class="cc-inc" data-r="priceInc">including VAT</div></div>
          <div class="cc-breakdown" data-r="breakdown"></div>
          <p class="cc-round" data-r="roundNote"></p>
          <div class="cc-cta">
            <button type="button" class="cc-buy" data-r="buy" disabled>Checkout wiring lands next</button>
            <button type="button" class="cc-ghost" data-copy>Copy link &mdash; send this to your installer</button>
            <p class="cc-legal">
              This lead is made to your measurements, so it cannot be returned if
              you change your mind. Please check your measurements before ordering.
              If it arrives faulty, or not as you specified, your rights are
              unaffected. <a href="/pages/returns-and-refunds">Returns and refunds</a>
            </p>
          </div>
        </div>
      </div>
      <div class="cc-bar" data-r="bar" hidden>
        <div class="cc-bar-price"><strong data-r="barPrice">&mdash;</strong><span data-r="barNote">including VAT</span></div>
        <button type="button" class="cc-buy cc-bar-buy" data-r="barBuy" disabled>Checkout lands next</button>
      </div>`;

    this.el = {};
    this.root.querySelectorAll("[data-r]").forEach((n) => { this.el[n.dataset.r] = n; });

    this.root.addEventListener("click", (e) => this.onClick(e));
    this.el.lenInput.addEventListener("change", (e) => this.setLen(Number(e.target.value)));
    this.el.lenRange.addEventListener("input", (e) => this.setLen(Number(e.target.value)));

    this.el.lenInput.max = this.data.pricing.maxLengthMm;
    this.el.lenRange.max = this.data.pricing.maxLengthMm;

    // Show the pinned bar only while the real price card is off screen. Two
    // totals on screen at once invites the reader to wonder which one is the
    // real one.
    if ("IntersectionObserver" in window) {
      this.barObserver = new IntersectionObserver(
        ([entry]) => { this.el.bar.hidden = entry.isIntersecting; },
        { rootMargin: "0px 0px -72px 0px" },
      );
      this.barObserver.observe(this.el.priceCard);
    }
  }

  onClick(e) {
    const copy = e.target.closest("[data-copy]");
    if (copy) {
      navigator.clipboard?.writeText(location.href).then(
        () => { copy.textContent = "Link copied"; setTimeout(() => { copy.textContent = "Copy link — send this to your installer"; }, 2200); },
        () => {},
      );
      return;
    }
    const same = e.target.closest("[data-same]");
    if (same) { this.state.notices = []; this.state.endB = this.state.endA; this.render(); return; }

    const len = e.target.closest("[data-len]");
    if (len) { this.setLen(this.state.lengthMm + Number(len.dataset.len)); return; }

    const b = e.target.closest("button.cc-opt");
    if (!b || b.disabled) return;
    this.state.notices = [];
    const sizeChanged = Boolean(b.dataset.f || b.dataset.csa);
    if (b.dataset.f) {
      this.state.family = b.dataset.f;
      if (!this.fam().cables.some((c) => c.csa === this.state.csa)) this.state.csa = this.fam().cables[0].csa;
    }
    if (b.dataset.csa) this.state.csa = Number(b.dataset.csa);
    if (b.dataset.col) this.state.colour = b.dataset.col;
    if (b.dataset.end) this.state[b.dataset.end] = b.dataset.v;
    if (sizeChanged) this.reconcileEnds();
    this.render();
  }

  setLen(v) {
    if (!Number.isFinite(v)) return;
    this.state.lengthMm = Math.max(10, Math.min(this.data.pricing.maxLengthMm, Math.round(v)));
    this.render();
  }

  // -- render ---------------------------------------------------------------
  render() {
    const d = this.data, s = this.state, c = this.cable();
    const money = (n) => "£" + n.toFixed(2);

    this.el.family.innerHTML = d.families.map((f) =>
      `<button type="button" class="cc-opt cc-wide" data-f="${f.key}" aria-pressed="${f.key === s.family}">${f.label}<small>${f.cables.length} size${f.cables.length > 1 ? "s" : ""}</small></button>`).join("");
    this.el.familyNote.textContent = "";

    this.el.csa.innerHTML = this.fam().cables.map((cc) =>
      `<button type="button" class="cc-opt" data-csa="${cc.csa}" aria-pressed="${cc.csa === s.csa}">${cc.csa}mm²<small>${cc.ampsFreeAir}A</small></button>`).join("");
    this.el.csaSpec.textContent =
      `${c.awg} AWG equivalent · ${c.odMm}mm outside diameter · ${c.kgPerM}kg per metre · ${c.mohmPerM} mΩ per metre`;

    this.el.colour.innerHTML = [["red", "Red"], ["black", "Black"], ["pair", "Matched pair"]].map(([k, l]) =>
      `<button type="button" class="cc-opt" data-col="${k}" aria-pressed="${k === s.colour}">${l}${k === "pair" ? "<small>one red, one black</small>" : ""}</button>`).join("");

    let anyBlocked = false;
    for (const side of ["endA", "endB"]) {
      const avail = d.studs.availability[String(c.csa)] ?? {};
      this.el[side].innerHTML = STUDS.map((st) => {
        const reason = studReason(d, c.csa, st);
        if (reason) {
          anyBlocked = true;
          return `<button type="button" class="cc-opt" disabled aria-disabled="true" title="${st} will not crimp onto ${c.csa}mm² cable: ${reason}">${st}<small>${reason}</small></button>`;
        }
        const badge = avail[st] === false ? `<span class="cc-badge">2–3 day lead</span>` : "";
        return `<button type="button" class="cc-opt" data-end="${side}" data-v="${st}" aria-pressed="${s[side] === st}">${st}<small>${d.studs.holeDiameters[st]}mm hole</small>${badge}</button>`;
      }).join("") +
      `<button type="button" class="cc-opt" data-end="${side}" data-v="cut" aria-pressed="${s[side] === "cut"}">Unterminated<small>cut and sealed</small></button>`;
    }
    this.el.endsNote.textContent = anyBlocked
      ? `Greyed-out studs cannot be crimped onto ${c.csa}mm² cable, so we do not offer them. Anything still selectable we will make.`
      : `Every stud size can be crimped onto ${c.csa}mm². Different sizes at each end cost no extra.`;

    this.el.lenInput.value = s.lengthMm;
    this.el.lenRange.value = Math.min(s.lengthMm, d.pricing.maxLengthMm);

    const p = priceAssembly({
      family: s.family, csa: c.csa, lengthMm: s.lengthMm,
      endA: s.endA === "cut" ? { type: "unterminated" } : { type: "lug", stud: s.endA },
      endB: s.endB === "cut" ? { type: "unterminated" } : { type: "lug", stud: s.endB },
    }, this.cfg());

    if (!p.ok) {
      this.el.priceBig.textContent = "—";
      this.el.priceInc.textContent = "We cannot price this combination";
      this.el.breakdown.innerHTML = "";
      this.el.roundNote.textContent = "";
      this.el.msgs.innerHTML = `<div class="cc-msg stop"><span class="cc-t">Not available</span><span>${p.reason}</span></div>`;
      this.writeUrl();
      return;
    }

    this.el.lenFacts.textContent =
      `Billed as ${p.billedLengthM.toFixed(1)}m · weighs about ${(p.billedLengthM * c.kgPerM).toFixed(2)}kg`;

    const msgs = [...s.notices, ...this.validate(p)];
    const blocked = msgs.some((m) => m.level === "stop");

    this.el.priceBig.textContent = blocked ? "—" : money(p.priceIncVat);
    this.el.priceInc.textContent = blocked ? "Not something we can make" : "including VAT, per lead";

    this.el.breakdown.innerHTML = blocked ? "" : [
      ...p.lines.map((l) => `<div class="cc-brow"><span class="cc-muted">${l.label}</span><span>${money(l.amountExVat)}</span></div>`),
      `<div class="cc-brow cc-sep"><span class="cc-muted">Subtotal excluding VAT</span><span>${money(p.subtotalExVat)}</span></div>`,
      `<div class="cc-brow"><span class="cc-muted">VAT</span><span>${money(p.vatAmount)}</span></div>`,
      `<div class="cc-brow"><span class="cc-muted">Calculated</span><span>${money(p.calculatedIncVat)}</span></div>`,
      `<div class="cc-brow cc-total"><span>You pay</span><span>${money(p.priceIncVat)}</span></div>`,
    ].join("");

    this.el.roundNote.textContent = blocked ? ""
      : p.roundingUpliftIncVat > 0 ? `Rounded up ${money(p.roundingUpliftIncVat)} to the next ${money(d.store.roundingStepIncVat)} step.`
      : `Lands exactly on a ${money(d.store.roundingStepIncVat)} step, so nothing is added.`;

    this.el.msgs.innerHTML = msgs.map((m, i) =>
      `<div class="cc-msg ${m.level}"><span class="cc-t">${m.t}</span><span>${m.d}</span>${m.fix ? `<button type="button" data-fix="${i}">${m.fix.label}</button>` : ""}</div>`).join("");
    this.el.msgs.querySelectorAll("[data-fix]").forEach((b) =>
      b.addEventListener("click", () => { msgs[Number(b.dataset.fix)].fix.run(); this.render(); }));

    this.el.buy.textContent = s.colour === "pair" ? "Add matched pair — checkout lands next" : "Checkout wiring lands next";

    this.el.barPrice.textContent = blocked ? "—" : money(p.priceIncVat);
    this.el.barNote.textContent = blocked
      ? "Not something we can make"
      : `${c.csa}mm² · ${(s.lengthMm / 1000).toFixed(2)}m · inc VAT`;
    this.el.barBuy.textContent = s.colour === "pair" ? "Add pair" : "Add to basket";

    this.drawDiagram(p);
    this.writeUrl();
  }

  // -- diagram --------------------------------------------------------------
  // Cable thickness and lug hole diameters are drawn at a constant, real scale
  // so an M6 end visibly differs from an M12 one. The RUN between them is
  // compressed on a log curve with a visible break symbol, because 20 metres
  // and 200 millimetres cannot share a linear scale on one screen. The
  // dimension line always carries the true figure.
  drawDiagram(p) {
    const PX_PER_MM = 2.6;
    const d = this.data, s = this.state, c = this.cable();
    const W = 700, midY = 96;
    const od = c.odMm * PX_PER_MM;
    const colours = s.colour === "red" ? ["var(--cc-accent)"] : s.colour === "black" ? ["var(--cc-ink)"] : ["var(--cc-accent)", "var(--cc-ink)"];

    const conductor = s.family === "plain" ? "var(--cc-copper)" : "var(--cc-tinned)";
    const geom = (end) => end === "cut" ? { w: 26, hole: 0 }
      : { hole: d.studs.holeDiameters[end] * PX_PER_MM, w: Math.max(58, d.studs.holeDiameters[end] * PX_PER_MM * 3.2) };
    const gA = geom(s.endA), gB = geom(s.endB);

    const minRun = 70;
    const maxRun = W - 2 * (Math.max(gA.w, gB.w) + 34) - 40;
    const t = Math.log(s.lengthMm / 100) / Math.log(d.pricing.maxLengthMm / 100);
    const run = Math.max(minRun, minRun + (Math.max(minRun, maxRun) - minRun) * t);

    const x0 = (W - (gA.w + run + gB.w)) / 2;
    const x1 = x0 + gA.w, x2 = x1 + run;
    const broken = s.lengthMm > 1500;

    const lug = (x, g, side) => g.hole === 0
      ? `<rect x="${x}" y="${midY - od / 2}" width="20" height="${od}" rx="2" fill="var(--cc-graphite)" opacity=".55"/>`
      : (() => {
          const h = Math.max(g.hole * 1.9, od * 1.15);
          return `<rect x="${x}" y="${midY - h / 2}" width="${g.w}" height="${h}" rx="${h / 2}" fill="none" stroke="var(--cc-graphite)" stroke-width="2"/>
                  <circle cx="${side === "L" ? x + g.w * 0.32 : x + g.w * 0.68}" cy="${midY}" r="${g.hole / 2}" fill="var(--cc-surface)" stroke="var(--cc-graphite)" stroke-width="2"/>`;
        })();

    let runs = "";
    colours.forEach((col, i) => {
      const off = colours.length === 1 ? 0 : (i === 0 ? -(od * 0.62) : od * 0.62);
      const y = midY + off - od / 2;
      if (broken) {
        const gap = 26, m = (x1 + x2) / 2;
        runs += `<rect x="${x1}" y="${y + od * 0.32}" width="${m - gap / 2 - x1}" height="${od * 0.36}" fill="${conductor}"/>
                 <rect x="${x1}" y="${y}" width="${m - gap / 2 - x1}" height="${od}" rx="${od / 3}" fill="${col}" opacity=".88"/>
                 <rect x="${m + gap / 2}" y="${y}" width="${x2 - m - gap / 2}" height="${od}" rx="${od / 3}" fill="${col}"/>
                 <path d="M ${m - gap / 2} ${y + od / 2} l 8 -${od * 0.55} l 10 ${od * 1.1} l 8 -${od * 0.55}" fill="none" stroke="${col}" stroke-width="2.5" stroke-linejoin="round"/>`;
      } else {
        runs += `<rect x="${x1}" y="${y + od * 0.32}" width="${run}" height="${od * 0.36}" fill="${conductor}"/>
                 <rect x="${x1}" y="${y}" width="${run}" height="${od}" rx="${od / 3}" fill="${col}" opacity=".88"/>`;
      }
    });

    const boot = (x, w) => `<rect x="${x}" y="${midY - od * 0.78 / 2 - od * 0.16}" width="${w}" height="${od * 0.78 + od * 0.32}" rx="3" fill="var(--cc-graphite)" opacity=".28"/>`;
    const cap = (x, txt, sub) =>
      `<text x="${x}" y="34" text-anchor="middle" font-size="13" fill="var(--cc-ink)" font-family="ui-monospace,monospace">${txt}</text>
       <text x="${x}" y="50" text-anchor="middle" font-size="10.5" fill="var(--cc-muted)" font-family="ui-monospace,monospace">${sub}</text>`;

    const a = s.endA === "cut" ? ["Cut end", "unterminated"] : [s.endA, d.studs.holeDiameters[s.endA] + "mm hole"];
    const b = s.endB === "cut" ? ["Cut end", "unterminated"] : [s.endB, d.studs.holeDiameters[s.endB] + "mm hole"];
    const dimY = midY + 62;

    this.el.diagram.innerHTML = `
      ${cap(x0 + gA.w / 2, a[0], a[1])}
      ${cap(x2 + gB.w / 2, b[0], b[1])}
      ${runs}
      ${boot(x1 - 4, 34)}
      ${boot(x2 - 30, 34)}
      ${lug(x0, gA, "L")}
      ${lug(x2, gB, "R")}
      <line x1="${x0 + gA.w * 0.32}" y1="${midY + 26}" x2="${x0 + gA.w * 0.32}" y2="${dimY + 6}" stroke="var(--cc-graphite)"/>
      <line x1="${x2 + gB.w * 0.68}" y1="${midY + 26}" x2="${x2 + gB.w * 0.68}" y2="${dimY + 6}" stroke="var(--cc-graphite)"/>
      <line x1="${x0 + gA.w * 0.32}" y1="${dimY}" x2="${x2 + gB.w * 0.68}" y2="${dimY}" stroke="var(--cc-graphite)"/>
      <rect x="${W / 2 - 76}" y="${dimY - 11}" width="152" height="22" fill="var(--cc-surface)"/>
      <text x="${W / 2}" y="${dimY + 5}" text-anchor="middle" font-size="12" fill="var(--cc-ink)" font-family="ui-monospace,monospace">${s.lengthMm}mm hole to hole</text>
      <text x="${W / 2}" y="202" text-anchor="middle" font-size="11.5" fill="var(--cc-muted)">${c.csa}mm² ${this.fam().label.toLowerCase()} · ${c.odMm}mm outside diameter${broken ? " · run shortened to fit" : ""}</text>`;

    this.el.diagramDesc.textContent =
      `${s.lengthMm}mm of ${c.csa}mm² ${this.fam().label} cable in ${s.colour === "pair" ? "a matched red and black pair" : s.colour}, with ${a[0]} at one end and ${b[0]} at the other.`;
  }
}

// ---------------------------------------------------------------------------
// Boot. On failure the server-rendered fallback is left exactly as it is and a
// short note is added. A customer who cannot use the tool must still be able to
// see what we sell and ring us.
// ---------------------------------------------------------------------------
async function boot() {
  const root = document.querySelector("[data-cc-configurator]");
  if (!root) return;

  const apiUrl = root.dataset.api;
  const fallback = root.querySelector("[data-cc-fallback]");

  if (!apiUrl) return;

  try {
    const payload = await loadPayload(apiUrl);
    if (!payload.families.length) throw new Error("no cable sizes available");
    if (fallback) fallback.hidden = true;
    const host = root.querySelector("[data-cc-app]");
    host.hidden = false;
    new Configurator(host, payload);
  } catch (err) {
    console.error("[cc-configurator]", err);
    const note = root.querySelector("[data-cc-error]");
    if (note) note.hidden = false;
  }
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
